// src/components/Admin/UserDetailModal.jsx
import React from 'react';
import { getExperienceLabel } from '../../utils/experienceLevels';

const UserDetailModal = ({ user, isOpen, onClose }) => {
    if (!isOpen || !user) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
    };

    const getAvatarColor = (gender) => {
        if (gender?.toLowerCase() === 'male') return 'bg-blue-100 text-blue-600';
        if (gender?.toLowerCase() === 'female') return 'bg-pink-100 text-pink-600';
        return 'bg-gray-100 text-gray-600';
    };

    // Check if profile is complete - using merged data
    const isProfileComplete = () => {
        // Required fields from both collections
        if (!user.firstName?.trim()) return false;
        if (!user.lastName?.trim()) return false;
        if (!user.phoneNumber?.trim()) return false;
        if (!user.city?.trim()) return false;
        if (!user.country?.trim()) return false;
        if (!user.gender) return false;
        if (!user.dateOfBirth) return false;
        
        // ID check for South Africans
        if (user.citizenship === 'South African') {
            if (!user.idNumber?.trim()) return false;
            if (!/^\d{13}$/.test(user.idNumber)) return false;
        }
        
        if (!user.lookingFor?.trim()) return false;
        if (!user.preferredJobLocation?.trim()) return false;
        if (!user.desiredSalary?.trim()) return false;
        if (!user.professionalSummary?.trim()) return false;
        if (user.skills?.length < 3) return false;
        if (user.workExperience?.length === 0 && user.education?.length === 0) return false;
        
        return true;
    };

    const getCompletionPercentage = () => {
        let completed = 0;
        let total = 11;
        
        if (user.firstName?.trim()) completed++;
        if (user.lastName?.trim()) completed++;
        if (user.phoneNumber?.trim()) completed++;
        if (user.city?.trim()) completed++;
        if (user.country?.trim()) completed++;
        if (user.gender) completed++;
        if (user.dateOfBirth) completed++;
        if (user.lookingFor?.trim()) completed++;
        if (user.preferredJobLocation?.trim()) completed++;
        if (user.desiredSalary?.trim()) completed++;
        if (user.professionalSummary?.trim()) completed++;
        if (user.skills?.length >= 3) completed++;
        if (user.workExperience?.length > 0 || user.education?.length > 0) completed++;
        
        if (user.citizenship === 'South African') {
            total++;
            if (user.idNumber?.trim() && /^\d{13}$/.test(user.idNumber)) completed++;
        }
        
        return Math.round((completed / total) * 100);
    };

    const complete = isProfileComplete();
    const percentage = getCompletionPercentage();

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="sticky top-0 float-right m-4 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md text-gray-400 hover:text-gray-600 transition-all">
                    <i className="fas fa-times"></i>
                </button>
                
                <div className="p-6 pt-0">
                    {/* User Header */}
                    <div className="flex items-center gap-4 pt-6 pb-6 border-b border-gray-200">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${getAvatarColor(user.gender)}`}>
                            {getInitials(user.firstName, user.lastName)}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-bold text-gradpath-dark">
                                    {user.firstName} {user.lastName}
                                </h2>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    complete ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                                }`}>
                                    {complete ? '✅ Complete' : `${percentage}% Complete`}
                                </span>
                            </div>
                            <p className="text-gray-500">{user.email}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                    {user.userType === 'student' ? 'Student' : 'Graduate'}
                                </span>
                                <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">
                                    {user.role || 'Candidate'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Profile Information */}
                    <div className="mt-6">
                        <h3 className="text-lg font-bold text-gradpath-dark mb-4">Profile Information</h3>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-500">Phone</label>
                                <p className="font-medium">{user.phoneNumber || 'Not provided'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Date of Birth</label>
                                <p className="font-medium">{user.dateOfBirth || 'Not provided'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Gender</label>
                                <p className="font-medium capitalize">{user.gender || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Citizenship</label>
                                <p className="font-medium">{user.citizenship || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">ID Number</label>
                                <p className="font-medium">{user.idNumber || 'Not provided'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Ethnicity</label>
                                <p className="font-medium">{user.ethnicity || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Has Disability</label>
                                <p className="font-medium">{user.hasDisability || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Member Since</label>
                                <p className="font-medium">{formatDate(user.createdAt)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Job Preferences */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gradpath-dark mb-4">Job Preferences</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-500">Looking For</label>
                                <p className="font-medium">{user.lookingFor || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Preferred Location</label>
                                <p className="font-medium">{user.preferredJobLocation || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Desired Salary</label>
                                <p className="font-medium">{user.desiredSalary || 'Not specified'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Willing to Relocate</label>
                                <p className="font-medium">{user.willingToRelocate ? '✅ Yes' : '❌ No'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Professional Links */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Links</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {user.linkedin && (
                                <div>
                                    <label className="text-sm text-gray-500">
                                        <i className="fab fa-linkedin text-blue-600 mr-1"></i> LinkedIn
                                    </label>
                                    <p className="font-medium">
                                        <a href={user.linkedin} target="_blank" rel="noopener noreferrer" className="text-gradpath-green hover:underline break-all">
                                            {user.linkedin}
                                        </a>
                                    </p>
                                </div>
                            )}
                            {user.portfolio && (
                                <div>
                                    <label className="text-sm text-gray-500">
                                        <i className="fas fa-folder-open text-gradpath-green mr-1"></i> Portfolio
                                    </label>
                                    <p className="font-medium">
                                        <a href={user.portfolio} target="_blank" rel="noopener noreferrer" className="text-gradpath-green hover:underline break-all">
                                            {user.portfolio}
                                        </a>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Skills & Experience */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gradpath-dark mb-4">Skills & Experience</h3>
                        
                        <div className="mb-4">
                            <label className="text-sm text-gray-500">Skills</label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {user.skills?.length > 0 ? (
                                    user.skills.map((skill, index) => (
                                        <span key={index} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded-full text-xs">
                                            {skill}
                                        </span>
                                    ))
                                ) : (
                                    <p className="text-gray-400 text-sm">No skills listed</p>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="text-sm text-gray-500">Languages</label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {user.languages?.length > 0 ? (
                                    user.languages.map((lang, index) => (
                                        <span key={index} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                                            {lang}
                                        </span>
                                    ))
                                ) : (
                                    <p className="text-gray-400 text-sm">No languages listed</p>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="text-sm text-gray-500">Experience Level</label>
                            <p className="font-medium">{user.experienceLevel ? getExperienceLabel(user.experienceLevel) : 'Not specified'}</p>
                        </div>

                        <div>
                            <label className="text-sm text-gray-500">Driver's Licence</label>
                            <p className="font-medium">{user.hasDriversLicence ? '✅ Yes' : '❌ No'}</p>
                        </div>
                    </div>

                    {/* Education - UPDATED with new fields */}
                    {user.education?.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gradpath-dark mb-4">Education</h3>
                            <div className="space-y-3">
                                {user.education.map((edu, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                                        <p className="font-bold">{edu.qualification}</p>
                                        <p className="text-sm text-gray-600">{edu.institution}</p>
                                        {(edu.startDate || edu.endDate) && (
                                            <p className="text-sm text-gray-500">
                                                {edu.startDate || 'Start date not specified'} 
                                                {edu.isCurrent ? ' - Present' : edu.endDate ? ` - ${edu.endDate}` : ''}
                                                {edu.isCurrent && (
                                                    <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                        Current
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                        {edu.keyHighlights && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                <span className="font-medium">Highlights:</span> {edu.keyHighlights}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Work Experience */}
                    {user.workExperience?.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gradpath-dark mb-4">Work Experience</h3>
                            <div className="space-y-3">
                                {user.workExperience.map((exp, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                                        <p className="font-bold">{exp.title} at {exp.company}</p>
                                        <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate || 'Present'}</p>
                                        {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Certifications & Awards - NEW */}
                    {user.certifications?.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                <i className="fas fa-certificate text-gradpath-green mr-2"></i>
                                Certifications & Awards
                            </h3>
                            <div className="space-y-3">
                                {user.certifications.map((cert, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                                        <p className="font-bold">{cert.name}</p>
                                        {cert.issuingOrganization && (
                                            <p className="text-sm text-gray-600">{cert.issuingOrganization}</p>
                                        )}
                                        {cert.year && (
                                            <p className="text-sm text-gray-500">{cert.year}</p>
                                        )}
                                        {cert.description && (
                                            <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* References - NEW */}
                    {user.references?.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                <i className="fas fa-address-book text-gradpath-green mr-2"></i>
                                References
                            </h3>
                            <div className="space-y-3">
                                {user.references.map((ref, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                                        <p className="font-bold">{ref.name}</p>
                                        {ref.title && <p className="text-sm text-gray-600">{ref.title}</p>}
                                        {ref.company && <p className="text-sm text-gray-600">{ref.company}</p>}
                                        {(ref.email || ref.phone) && (
                                            <div className="mt-1 text-sm text-gray-500">
                                                {ref.email && <span>📧 {ref.email}</span>}
                                                {ref.phone && <span className="ml-3">📱 {ref.phone}</span>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Professional Summary */}
                    {user.professionalSummary && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Summary</h3>
                            <p className="text-gray-600">{user.professionalSummary}</p>
                        </div>
                    )}

                    {/* Account Status */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gradpath-dark mb-4">Account Status</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-500">Profile Status</label>
                                <p className={`font-medium ${complete ? 'text-green-600' : 'text-yellow-600'}`}>
                                    {complete ? '✅ Complete' : `${percentage}% Complete`}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Role</label>
                                <p className="font-medium capitalize">{user.role || 'Candidate'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">User Type</label>
                                <p className="font-medium capitalize">{user.userType || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500">Status</label>
                                <p className="font-medium capitalize">{user.status || 'Active'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailModal;